import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { MsalProvider } from '@azure/msal-react';

import theme from './theme';
import { msalInstance } from './auth/authConfig';
import { MockAuthProvider, useAuth } from './auth';
import AppShell from './components/layout/AppShell';
import PortalShell from './components/layout/PortalShell';
import PortalRedirect from './components/auth/PortalRedirect';
import LoginPage from './pages/auth/LoginPage';

// Pages — Dashboard
import DashboardPage from './pages/dashboard/DashboardPage';

// Pages — Orders
import PartOrdersPage from './pages/orders/PartOrdersPage';
import PartOrderDetailPage from './pages/orders/PartOrderDetailPage';
import PartReplenishmentPage from './pages/orders/PartReplenishmentPage';
import PartReplenishmentDetailPage from './pages/orders/PartReplenishmentDetailPage';
import SupplierQuotesPage from './pages/orders/SupplierQuotesPage';

// Pages — Shipments
import ShipmentsPage from './pages/shipments/ShipmentsPage';
import DispatchBoardPage from './pages/shipments/DispatchBoardPage';
import ShipmentDetailPage from './pages/shipments/ShipmentDetailPage';
import ReplenishmentShipmentsPage from './pages/shipments/ReplenishmentShipmentsPage';
import ReplenishmentShipmentDetailPage from './pages/shipments/ReplenishmentShipmentDetailPage';

// Pages — Inventory
import InventoryPage from './pages/inventory/InventoryPage';
import SmartStockingPage from './pages/inventory/SmartStockingPage';
import InventorySearchPage from './pages/inventory/InventorySearchPage';
import PartInventoryDetailPage from './pages/inventory/PartInventoryDetailPage';
import ReverseLogisticsPage from './pages/inventory/ReverseLogisticsPage';
import RMADetailPage from './pages/inventory/RMADetailPage';
import SupplierPricingPage from './pages/inventory/SupplierPricingPage';

// Pages — Accounting
import PurchaseOrdersPage from './pages/accounting/PurchaseOrdersPage';
import PurchaseOrderDetailPage from './pages/accounting/PurchaseOrderDetailPage';
import PaymentsPage from './pages/accounting/PaymentsPage';
import PaymentDetailPage from './pages/accounting/PaymentDetailPage';
import ReportsPage from './pages/accounting/ReportsPage';

// Pages — Settings
import SettingsPage from './pages/settings/SettingsPage';
import StockLocationsPage from './pages/settings/StockLocationsPage';
import StockLocationDetailPage from './pages/settings/StockLocationDetailPage';
import SuppliersPage from './pages/inventory/SuppliersPage';
import SupplierDetailPage from './pages/inventory/SupplierDetailPage';
import UsersPage from './pages/settings/UsersPage';
import NotificationRulesPage from './pages/settings/NotificationRulesPage';
import IntegrationsPage from './pages/settings/IntegrationsPage';
import CustomersPage from './pages/settings/CustomersPage';

// Pages — Customer Portal
import PortalTicketsPage from './pages/portal/PortalTicketsPage';
import PortalTicketDetailPage from './pages/portal/PortalTicketDetailPage';
import PortalDispatchPage from './pages/portal/PortalDispatchPage';
import PortalInventoryPage from './pages/portal/PortalInventoryPage';
import PortalLocationsPage from './pages/portal/PortalLocationsPage';
import PortalVerifyPage from './pages/portal/PortalVerifyPage';

// ── Auth guard — redirects unauthenticated users to /login ───────────────────
function AuthGuard({ children }) {
  const { isLoggedIn } = useAuth();
  return isLoggedIn ? children : <Navigate to="/login" replace />;
}

// Redirect helpers for parameterized routes that moved from /settings to /inventory
function RedirectStockLocationDetail() {
  const { id } = useParams();
  return <Navigate to={`/inventory/stock-locations/${id}`} replace />;
}
function RedirectSupplierDetail() {
  const { id } = useParams();
  return <Navigate to={`/inventory/suppliers/${id}`} replace />;
}

export default function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <MockAuthProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <Routes>
              {/* ── Login page — public, no auth required ── */}
              <Route path="/login" element={<LoginPage />} />

              {/* ── Portal Verify — bare mobile page, no auth required ── */}
              <Route path="/portal/verify/:token" element={<PortalVerifyPage />} />

              {/* ── Internal app — requires login ── */}
              <Route element={<AuthGuard><AppShell /></AuthGuard>}>
                {/* Dashboard */}
                <Route index element={<DashboardPage />} />

                {/* Orders */}
                <Route path="/orders/parts" element={<PartOrdersPage />} />
                <Route path="/orders/parts/:id" element={<PartOrderDetailPage />} />
                <Route path="/orders/quotes" element={<SupplierQuotesPage />} />
                <Route path="/orders/part-replenishment" element={<PartReplenishmentPage />} />
                <Route path="/orders/part-replenishment/:id" element={<PartReplenishmentDetailPage />} />

                {/* Shipments */}
                <Route path="/shipments" element={<ShipmentsPage />} />
                <Route path="/shipments/dispatch" element={<DispatchBoardPage />} />
                <Route path="/shipments/dispatch/:id" element={<ShipmentDetailPage />} />
                <Route path="/shipments/replenishment-shipments" element={<ReplenishmentShipmentsPage />} />
                <Route path="/shipments/replenishment-shipments/:id" element={<ReplenishmentShipmentDetailPage />} />

                {/* Inventory */}
                <Route path="/inventory" element={<InventoryPage />} />
                <Route path="/inventory/search" element={<InventorySearchPage />} />
                <Route path="/inventory/search/:partId" element={<PartInventoryDetailPage />} />
                <Route path="/inventory/reverse-logistics" element={<ReverseLogisticsPage />} />
                <Route path="/inventory/reverse-logistics/:id" element={<RMADetailPage />} />
                <Route path="/inventory/supplier-pricing"  element={<SupplierPricingPage />} />
                <Route path="/inventory/stock-locations" element={<StockLocationsPage />} />
                <Route path="/inventory/stock-locations/:id" element={<StockLocationDetailPage />} />
                <Route path="/inventory/suppliers" element={<SuppliersPage />} />
                <Route path="/inventory/suppliers/:id" element={<SupplierDetailPage />} />
                <Route path="/inventory/smart-stocking" element={<SmartStockingPage />} />

                {/* Accounting */}
                <Route path="/accounting/purchase-orders"     element={<PurchaseOrdersPage />} />
                <Route path="/accounting/purchase-orders/:id" element={<PurchaseOrderDetailPage />} />
                <Route path="/accounting/payments"            element={<PaymentsPage />} />
                <Route path="/accounting/payments/:id"        element={<PaymentDetailPage />} />
                <Route path="/accounting/reports"             element={<ReportsPage />} />

                {/* Settings */}
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/settings/stock-locations" element={<StockLocationsPage />} />
                <Route path="/settings/stock-locations/:id" element={<RedirectStockLocationDetail />} />
                <Route path="/settings/suppliers" element={<Navigate to="/inventory/suppliers" replace />} />
                <Route path="/settings/suppliers/:id" element={<RedirectSupplierDetail />} />
                <Route path="/settings/customers" element={<CustomersPage />} />
                <Route path="/settings/users" element={<UsersPage />} />
                <Route path="/settings/notifications" element={<NotificationRulesPage />} />
                <Route path="/settings/integrations" element={<IntegrationsPage />} />
              </Route>

              {/* ── Customer Portal — requires login ── */}
              <Route path="/portal" element={<AuthGuard><PortalRedirect /></AuthGuard>}>
                <Route element={<PortalShell />}>
                  <Route index element={<Navigate to="/portal/tickets" replace />} />
                  <Route path="tickets" element={<PortalTicketsPage />} />
                  <Route path="tickets/:id" element={<PortalTicketDetailPage />} />
                  <Route path="dispatch" element={<PortalDispatchPage />} />
                  <Route path="inventory" element={<PortalInventoryPage />} />
                  <Route path="fulfillment" element={<Navigate to="/portal/tickets" replace />} />
                  <Route path="locations" element={<PortalLocationsPage />} />
                </Route>
              </Route>
            </Routes>
          </BrowserRouter>
        </ThemeProvider>
      </MockAuthProvider>
    </MsalProvider>
  );
}
